/**
 * A worksheet's settings, and where they live in the address.
 *
 * Everything a sheet is drawn from is in the fragment, so a teacher can keep a
 * link and print the same sheet again next term, or send it to a colleague.
 * The fragment rather than the query string because a typed list of
 * characters can run long, and CloudFront answers a URL over 8,192 bytes with
 * a 414 before the page is ever served. A fragment never leaves the browser.
 *
 * The parameters are named after the settings and only the ones moved off
 * their default are written, the way `url-state.ts` writes the converters'.
 * An address is the one input to the page that did not come from the person
 * looking at it, so anything unrecognised falls back to the default.
 */

import { hskBands } from "../content/hsk/hsk-band.js";

/** Where the words on a sheet come from. */
export type WorksheetSource = "typed" | "list" | "saved" | "mine";

/** The paper the sheet is laid out for. */
export type WorksheetPaper = "a4" | "letter";

/** Whether a sheet keeps the order its words were given in. */
export type WorksheetOrder = "given" | "shuffled";

/**
 * What every kind of sheet is drawn from: where its words come from, the
 * paper it is laid out for and the order it prints them in. Each kind adds
 * the parts it prints.
 */
export interface SheetSourceState {
  readonly source: WorksheetSource;

  /** The characters or words typed in, when the source is `typed`. */
  readonly text: string;

  /** A List's path under `/cidan/`, when the source is `list`. */
  readonly list: string;

  /**
   * One of the reader's own Lists, as `<list>` or `<list>/<part>` by id, when
   * the source is `mine`.
   */
  readonly mine: string;

  /** The tag the saved words are narrowed to, or empty for all of them. */
  readonly tag: string;

  /**
   * The HSK band the saved words are narrowed to, as `HSK 2`, or empty for
   * every band. A sheet whose page offers no band select keeps it empty.
   */
  readonly band: string;

  /**
   * Which part of the saved words or an own List prints, counting from one,
   * when there are too many for one sheet. See `saved-parts.ts`.
   */
  readonly part: number;

  readonly paper: WorksheetPaper;

  readonly order: WorksheetOrder;

  /**
   * What a shuffled sheet is shuffled from. The same seed deals the same
   * order, so a kept link prints last week's sheet again.
   */
  readonly seed: string;
}

/** Where a sheet nobody has touched takes its words from. */
export const defaultSource: SheetSourceState = {
  source: "typed",
  text: "",
  list: "",
  mine: "",
  tag: "",
  band: "",
  part: 1,
  paper: "a4",
  order: "given",
  seed: "",
};

/** The guide lines inside a practice square. */
export type WorksheetGrid = "mi" | "tian" | "plain";

/** The ink the copies to trace are printed in. */
export type WorksheetInk = "red" | "grey";

/** A number the sheet works out for itself unless one is chosen. */
export type Chosen = number | "auto";

/** The square sizes on offer, in millimetres on paper. */
export const cellRange = { min: 10, max: 24, step: 2 } as const;

/** The most copies to trace a character can ask for. */
export const maxTrace = 8;

/** The most rows of squares a character can ask for. */
export const maxRows = 4;

/** Everything a character writing sheet is drawn from. */
export interface WorksheetState extends SheetSourceState {
  /**
   * Whether the sheet has its writing practice pages.
   *
   * Each character with its strokes in order, a model in black, copies to
   * trace and empty squares to fill.
   */
  readonly practice: boolean;

  /**
   * Whether the sheet has its recall test pages.
   *
   * Each character's reading and meaning over empty squares, for writing it
   * from memory. They follow the practice pages.
   */
  readonly recall: boolean;

  /**
   * Whether the stroke order reference pages are printed last.
   *
   * Each character drawn large with an arrow and a number on every stroke.
   * After a recall test they are its answers as well.
   */
  readonly key: boolean;

  /** The guides in every square: 米字格, 田字格 or none. */
  readonly grid: WorksheetGrid;

  /** The side of a practice square in millimetres, or `auto` to fit the row. */
  readonly cell: Chosen;

  readonly ink: WorksheetInk;

  /** How many copies to trace follow the model, or `auto` by stroke count. */
  readonly trace: Chosen;

  /** How many rows of squares each character gets, or `auto`. */
  readonly rows: Chosen;
}

/**
 * A sheet nobody has touched.
 *
 * Everything under More settings defaults to what the sheet works out for
 * itself in `worksheet-layout.ts`: a 米字格, red 描红, squares and copies
 * sized to the characters, and the order the words were given in. Most
 * people never open it.
 */
export const defaultState: WorksheetState = {
  ...defaultSource,
  practice: true,
  recall: false,
  key: false,
  grid: "mi",
  cell: "auto",
  ink: "red",
  trace: "auto",
  rows: "auto",
};

/** The settings under More settings, which an untouched sheet leaves alone. */
export const moreSettings = [
  "grid",
  "cell",
  "ink",
  "trace",
  "rows",
  "order",
] as const satisfies readonly (keyof WorksheetState)[];

/** Whether a sheet has moved any of the settings under More settings. */
export function changesMoreSettings(state: WorksheetState): boolean {
  return moreSettings.some((name) => state[name] !== defaultState[name]);
}

/** One of the values on offer, or the default for anything else. */
export function oneOf<Value extends string>(
  given: string | null,
  offered: readonly Value[],
  otherwise: Value,
): Value {
  return offered.find((value) => value === given) ?? otherwise;
}

/**
 * A number the address chose, or `auto`.
 * @param range - What is on offer. A number off it, or off its steps, is
 * read as `auto`.
 */
function chosen(
  given: string | null,
  range: { readonly min: number; readonly max: number; readonly step?: number },
): Chosen {
  const value = Number(given);
  const step = range.step ?? 1;

  return given !== null &&
    /^\d+$/u.test(given) &&
    value >= range.min &&
    value <= range.max &&
    (value - range.min) % step === 0
    ? value
    : "auto";
}

/** A seed as the address writes it: a few letters and digits, or none. */
function seedOf(given: string | null): string {
  return (given ?? "").replace(/[^0-9a-z]/giu, "").slice(0, 12);
}

/** A tick box's setting, as the address writes it. */
export function flag(given: string | null, otherwise: boolean): boolean {
  return given === "1" ? true : given === "0" ? false : otherwise;
}

/** The longest tag an address is read for. */
const maxTag = 100;

/** A part's number from the address: a whole number from one, or one. */
function partOf(given: string | null): number {
  const part = Number(given);

  return Number.isInteger(part) && part >= 1 && part <= 999 ? part : 1;
}

/**
 * Read where a sheet's words come from out of an address's parameters.
 * @param parameters - The fragment, parsed.
 */
export function readSheetSource(parameters: URLSearchParams): SheetSourceState {
  const d = defaultSource;

  return {
    source: oneOf(
      parameters.get("source"),
      ["typed", "list", "saved", "mine"],
      d.source,
    ),
    text: parameters.get("text") ?? d.text,
    list: (parameters.get("list") ?? d.list).replace(/[^0-9a-z/-]/giu, ""),
    mine: (parameters.get("mine") ?? d.mine)
      .replace(/[^0-9a-z/_-]/giu, "")
      .slice(0, maxTag),
    tag: (parameters.get("tag") ?? d.tag).trim().slice(0, maxTag),
    band: oneOf(parameters.get("band"), ["", ...hskBands], d.band),
    part: partOf(parameters.get("part")),
    paper: oneOf(parameters.get("paper"), ["a4", "letter"], d.paper),
    order: oneOf(parameters.get("order"), ["given", "shuffled"], d.order),
    seed: seedOf(parameters.get("seed")),
  };
}

/**
 * Read a sheet's settings out of an address fragment.
 * @param hash - `location.hash`, with or without its `#`.
 * @returns The settings, with the defaults wherever the fragment said nothing
 * usable.
 */
export function readWorksheetState(hash: string): WorksheetState {
  const parameters = new URLSearchParams(hash.replace(/^#/u, ""));
  const d = defaultState;

  return {
    ...readSheetSource(parameters),
    practice: flag(parameters.get("practice"), d.practice),
    recall: flag(parameters.get("recall"), d.recall),
    key: flag(parameters.get("key"), d.key),
    grid: oneOf(parameters.get("grid"), ["mi", "tian", "plain"], d.grid),
    cell: chosen(parameters.get("cell"), cellRange),
    ink: oneOf(parameters.get("ink"), ["red", "grey"], d.ink),
    trace: chosen(parameters.get("trace"), { min: 0, max: maxTrace }),
    rows: chosen(parameters.get("rows"), { min: 1, max: maxRows }),
  };
}

/** A tick box's setting, the way the address writes it. */
export function bit(value: boolean): string {
  return value ? "1" : "0";
}

/**
 * Write where a sheet's words come from into an address's parameters, only
 * what differs from the defaults.
 *
 * The text is written only for a typed sheet, the List only for a List's and
 * the tag only for saved words and the own List only for an own List's, so
 * switching source and back does not leave the other one in the address.
 */
export function writeSheetSource(
  parameters: URLSearchParams,
  state: SheetSourceState,
): void {
  const d = defaultSource;
  if (state.source !== d.source) {
    parameters.set("source", state.source);
  }
  if (state.source === "typed" && state.text !== d.text) {
    parameters.set("text", state.text);
  }
  if (state.source === "list" && state.list !== d.list) {
    parameters.set("list", state.list);
  }
  if (state.source === "saved" && state.tag !== d.tag) {
    parameters.set("tag", state.tag);
  }
  if (state.source === "saved" && state.band !== d.band) {
    parameters.set("band", state.band);
  }
  if (state.source === "mine" && state.mine !== d.mine) {
    parameters.set("mine", state.mine);
  }
  if (
    (state.source === "saved" || state.source === "mine") &&
    state.part !== d.part
  ) {
    parameters.set("part", String(state.part));
  }
  if (state.paper !== d.paper) {
    parameters.set("paper", state.paper);
  }
  if (state.order !== d.order) {
    parameters.set("order", state.order);
    // A seed only orders a shuffled sheet, and would only lengthen the link
    // of one in the given order.
    if (state.seed !== d.seed) {
      parameters.set("seed", state.seed);
    }
  }
}

/**
 * The fragment for a sheet, holding only what differs from the defaults.
 * @param state - The sheet's settings.
 * @returns The fragment without its `#`, empty for an untouched sheet.
 */
export function worksheetFragment(state: WorksheetState): string {
  const parameters = new URLSearchParams();
  const d = defaultState;
  const write = (name: string, value: string, otherwise: string): void => {
    if (value !== otherwise) {
      parameters.set(name, value);
    }
  };

  writeSheetSource(parameters, state);
  write("practice", bit(state.practice), bit(d.practice));
  write("recall", bit(state.recall), bit(d.recall));
  write("key", bit(state.key), bit(d.key));
  write("grid", state.grid, d.grid);
  write("cell", String(state.cell), String(d.cell));
  write("ink", state.ink, d.ink);
  write("trace", String(state.trace), String(d.trace));
  write("rows", String(state.rows), String(d.rows));

  return parameters.toString();
}
